import InfoModal from 'App/Components/InfoModal';
import LazyImg from 'App/Components/LazyImg';
import { useCopyrightDate, useObserveVisibility } from 'App/hooks';
import { useAnalytics } from 'App/Services/Analytics';
import useTermsAndPrivacyContent from 'App/Services/LegalPages/game/useTermsAndPrivacyContent';

import logo from '../../../../assets/footer_logo.svg';
import upButton from '../../../../assets/up-arrow.svg';
import polyglot from '../../../../localization';
import localeSettings from '../../../../localization/settings.json';
import './styles.scss';

/**
 * Renders the localized landing-page footer: branding, legal links (Terms, Privacy, Cookies),
 * copyright text, and a “scroll to top” button on ≥768px screens. Legal links open in a modal
 * (content resolved via `useTermsAndPrivacyContent`), while preserving the hrefs for fallback.
 *
 * @param {object} props - Component props.
 * @param {"bg" | "en" | "pt" | "ro" | "escl" | "espe" | "sr"} props.lang - Locale key used to fetch all translated strings from `polyglot`.
 * @param {boolean} [props.useLocalLegalFiles=false] - When true, the hook loads local legal HTML before the remote CMS URL.
 * @param {string} props.path - Current route or locale subpath used by the hook when constructing local/legal paths.
 * @returns {JSX.Element} - Footer markup with modal for legal content.
 * @function Footer
 **/
const Footer = ({ lang, path, useLocalLegalFiles = false }) => {
    const currentYear = useCopyrightDate();
    const { sendGameDesignEvent, sendGoogleEvent } = useAnalytics();
    const visibleRef = useObserveVisibility(() => {
        sendGameDesignEvent('HP:FooterVisible');
        sendGoogleEvent('hp_footer_visible');
    });
    const { loading, isModalOpen, modalContent, localLegalUrls, showModal, hideModal } =
        useTermsAndPrivacyContent({
            fetchLocal: useLocalLegalFiles,
            localeSettings,
            lang,
            path,
        });

    return (
        <>
            <footer className='footer'>
                <div className='footer_main wrapper'>
                    <div className='footer_main-wrapper container'>
                        <div className='footer_about'>
                            <div className='footer_about__title-wrapper'>
                                <h3 className='footer_about__title'>
                                    {polyglot.t(`${lang}.footer.main.title`)}
                                </h3>
                                <img
                                    src={upButton}
                                    className='scroll-button-mobile'
                                    onClick={() => {
                                        window.scrollTo({
                                            top: 0,
                                            behavior: 'smooth',
                                        });
                                    }}
                                    alt='up button'
                                    loading='lazy'
                                />
                            </div>
                            <p className='footer_about__text'>
                                {polyglot.t(`${lang}.footer.main.aboutText`)}
                            </p>
                            <p className='footer_about__highlight'>
                                {polyglot.t(`${lang}.footer.main.haveFun`)}
                            </p>
                        </div>
                        <div className='footer_terms'>
                            <div className='footer_logo'>
                                <LazyImg
                                    src={logo}
                                    width={113}
                                    height={90}
                                    alt={polyglot.t(`${lang}.footer.alt.logo`)}
                                />
                            </div>
                            <div className='footer_terms-wrapper'>
                                <img
                                    src={upButton}
                                    className='scroll-button'
                                    onClick={() => {
                                        window.scrollTo({
                                            top: 0,
                                            behavior: 'smooth',
                                        });
                                    }}
                                    alt='up button'
                                    loading='lazy'
                                />
                                <p className='footer_terms-title'>
                                    {polyglot.t(`${lang}.footer.main.termsTitle`)}
                                </p>
                                <div className='footer_terms-links'>
                                    <a
                                        href={localLegalUrls.terms}
                                        onClick={(e) => showModal(e, 'terms')}>
                                        {polyglot.t(`${lang}.footer.main.termLinkText`)}
                                    </a>
                                    &nbsp;|&nbsp;
                                    <a
                                        href={localLegalUrls.privacy}
                                        onClick={(e) => showModal(e, 'privacy')}>
                                        {polyglot.t(`${lang}.footer.main.policyLinkText`)}
                                    </a>
                                </div>
                                <div>
                                    <a
                                        href={localLegalUrls.cookies}
                                        onClick={(e) => showModal(e, 'cookies')}>
                                        {polyglot.t(`${lang}.footer.main.cookieLinkText`)}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='footer_copyright wrapper' ref={visibleRef}>
                    <div className='container'>
                        <p>
                            ©{currentYear} {polyglot.t(`${lang}.footer.copyrightText`)}
                        </p>
                    </div>
                </div>
            </footer>
            {isModalOpen && (
                <InfoModal
                    loading={loading}
                    onClose={hideModal}
                    title={modalContent ? modalContent.title : ''}
                    content={modalContent ? modalContent.content : ''}
                />
            )}
        </>
    );
};

export default Footer;
